import React from 'react';
import Tooltip from '@mui/material/Tooltip';
import { CAMPAIGN_THEME, couponAmount } from 'components/CampaignFrame';
import { campaignsVisible } from 'util/campaignVisibility';

/*
The PRODUCT-LEVEL campaign on a product card - and only the product level. Seller-tier and
platform-tier offers are deliberately NOT shown here: a card advertises just the coupon/gift that
sits on THIS item. One full-width bar, styled to match the product-coupon strip in the app's card
design (light-blue tinted, blue coupon icon, chevron on the right):

  [icon]  Product Coupon: Flat ₹XX OFF      >     (tooltip: "coupon is given per lot")
  [icon]  Product Gift: <gift info>          >     (tooltip: "gift is given on item purchase")

`product.activeCoupon` is the product-tier campaign (attachOffers only ever hangs the product tier
there). Its rupee/gift detail is present only when the API let this visitor see prices; when absent
we keep the label and drop the detail so nothing looks empty.
*/

// The strip is painted from the same campaign palette as the card's frame and corner ribbon (see
// components/CampaignFrame), so a gift card is orange end to end and a coupon card is blue end to
// end. Tinted fill, matching hairline, deep text, accent chevron - the offer row from design 2a.

// The gap above/below the panel. Exported because the space ABOVE it is owned by the card's own
// padding (see UserProfileCard), which reads this to match.
export const OFFER_PANEL_GAP = 12;

// A coupon/ticket mark (Material "local_activity" shape).
const CouponIcon = ({ color }) => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill={color} aria-hidden="true" style={{ flexShrink: 0 }}>
    <path d="M20 6H4c-1.1 0-2 .9-2 2v3a2 2 0 0 1 0 4v3c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2v-3a2 2 0 0 1 0-4V8c0-1.1-.9-2-2-2zm-6 11h-2v-2h2v2zm0-4h-2v-2h2v2zm0-4h-2V7h2v2z" />
  </svg>
);

const ChevronRight = ({ color }) => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2"
    strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={{ flexShrink: 0 }}>
    <path d="M9 6l6 6-6 6" />
  </svg>
);

const OfferPanel = (props) => {
  // Withheld from guests entirely, along with the rest of the campaign layer - a visitor who cannot
  // place an order has nothing to redeem, so the strip is dropped rather than blurred (see
  // util/campaignVisibility).
  if (!campaignsVisible()) return null;
  const p = props.product || {};
  const c = p.activeCoupon;
  // Product tier only. activeCoupon is always product-tier, but guard defensively.
  if (!c || (c.tier && c.tier !== 'product')) return null;

  const isGift = c.type === 'gift';
  const theme = isGift ? CAMPAIGN_THEME.gift : CAMPAIGN_THEME.coupon;
  // Blurred for logged-out guests, the same way price is - they must sign in to read the offer.
  const blur = Boolean(props.blur);
  const blurStyle = blur ? { filter: 'blur(4px)', userSelect: 'none', pointerEvents: 'none' } : {};

  let detail;
  if (isGift) {
    const info = c.gift
      ? [c.gift.description, c.gift.brand].filter(Boolean).join(' - ')
      : '';
    detail = `Product Gift:${info ? ` ${info}` : ''}`;
  } else {
    const off = couponAmount(c.coupon_value);
    detail = `Product Coupon:${off ? ` Flat ₹${off} OFF` : ''}`;
  }

  const tip = isGift ? 'gift is given on item purchase' : 'coupon is given per lot';

  return (
    // whiteSpace normal overrides the card footer's inherited `white-space: nowrap` so the text
    // wraps inside the card; `style` is a passthrough so the card can correct its own top spacing.
    <div style={{ marginBottom: OFFER_PANEL_GAP, whiteSpace: 'normal', ...(props.style || {}) }}>
      <Tooltip title={tip} arrow>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            width: '100%',
            padding: '9px 12px',
            background: theme.soft,
            border: `1px solid ${theme.softLine}`,
            borderRadius: 10,
            cursor: 'default',
            ...blurStyle
          }}
        >
          {isGift
            ? <span aria-hidden="true" style={{ fontSize: 19, lineHeight: 1, flexShrink: 0 }}>🎁</span>
            : <CouponIcon color={theme.accent} />}
          <span style={{
            flex: 1,
            minWidth: 0,
            color: theme.deep,
            fontWeight: 700,
            fontSize: 13.5,
            lineHeight: 1.3,
            overflowWrap: 'anywhere'
          }}>
            {detail}
          </span>
          <ChevronRight color={theme.accent} />
        </div>
      </Tooltip>
    </div>
  );
};

export default OfferPanel;
